import React from 'react';
import { Navbar } from '../components/layout/Navbar';
import { Footer } from '../components/layout/Footer';
import { MessageSquare, Zap, Shield, Smartphone, Bell, Hash, Sparkles } from 'lucide-react';

export const FeaturesPage: React.FC = () => {
  const features = [
    {
      icon: Hash,
      title: 'Organized Channels & Groups',
      desc: 'Organize team discussions by department, project, or topic. Keep announcement channels clean and private channels secure.',
      iconColor: 'text-[#3a9efd]',
      iconBg: 'bg-[#3a9efd]/10',
    },
    {
      icon: Zap,
      title: 'Sub-millisecond Realtime Messaging',
      desc: 'Built on top of Spring Boot WebSocket STOMP message brokers with automatic reconnection and forward-sync pagination.',
      iconColor: 'text-[#f7a400]',
      iconBg: 'bg-[#f7a400]/10',
    },
    {
      icon: MessageSquare,
      title: 'Contextual Threaded Replies',
      desc: 'Keep channel chatter focused by spinning off deep discussions into dedicated thread sidebars without cluttering the main stream.',
      iconColor: 'text-[#292a73]',
      iconBg: 'bg-[#292a73]/10',
    },
    {
      icon: Shield,
      title: 'Strict Tenant Isolation',
      desc: 'PostgreSQL-backed tenant authorization at database and service boundaries. Scoped idempotency prevents duplicate messages.',
      iconColor: 'text-[#3e4491]',
      iconBg: 'bg-[#3e4491]/10',
    },
    {
      icon: Smartphone,
      title: 'Native Cross-Platform Apps',
      desc: 'First-class desktop apps powered by Tauri v2 and mobile apps built with React Native Expo 51.',
      iconColor: 'text-[#3a9efd]',
      iconBg: 'bg-[#3a9efd]/10',
    },
    {
      icon: Bell,
      title: 'Instant Push Notifications',
      desc: 'Cross-platform device registry binding tokens to authenticated user JWT principals for reliable mobile alerts.',
      iconColor: 'text-[#f7a400]',
      iconBg: 'bg-[#f7a400]/10',
    },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-[#F7F9FB] text-[#1a1b4b] antialiased selection:bg-[#3a9efd]/20 selection:text-[#1a1b4b]">
      <Navbar />
      <main className="flex-1 pt-28 pb-20">
        <div className="container mx-auto max-w-7xl px-4 sm:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
            <div className="inline-flex items-center gap-2 px-3.5 py-1 rounded-full bg-[#292a73]/10 border border-[#3e4491]/20 text-[#292a73] text-xs font-bold">
              <Sparkles className="h-3.5 w-3.5 text-[#f7a400]" />
              <span>Platform Capabilities</span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-black tracking-tight text-[#1a1b4b]">
              Built for{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#292a73] via-[#3a9efd] to-[#f7a400]">
                modern collaboration.
              </span>
            </h1>
            <p className="text-base text-[#3e4491] font-medium leading-relaxed max-w-2xl mx-auto">
              Deep dive into the architectural features and engineering advantages that power Sahaj Connect across Web, Desktop, and Mobile.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((f, i) => {
              const Icon = f.icon;
              return (
                <div
                  key={i}
                  className="p-7 rounded-2xl border-2 border-[#3e4491]/20 bg-white shadow-sm hover:shadow-xl hover:shadow-[#292a73]/10 hover:border-[#3a9efd] transition-all space-y-3.5"
                >
                  <div className={`w-11 h-11 rounded-xl ${f.iconBg} ${f.iconColor} flex items-center justify-center`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="font-bold text-lg text-[#1a1b4b]">{f.title}</h3>
                  <p className="text-xs text-[#3e4491] leading-relaxed font-medium">{f.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};
